import { useCallback, useEffect, useRef, useState } from 'react';
import { projectId } from '../utils/supabase/info';
import type { DiscoveryGrid, GridCell } from '../utils/discoveryGrid';
import { patchGridCell } from '../utils/discoveryGridPatch';

/**
 * Loads the discovery grid payload (GET /discovery-grid/:companyId) for the
 * focused engagement. One data path for the client detail tab and the modal
 * (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §5).
 *
 * Changed-since-last-visit (§4.6): the first load of a session carries the
 * server's `changed` flags against the viewer's last snapshot; the hook then
 * records the visit (POST …/seen) and keeps those cells flagged through the
 * session's reloads, so acting on one cell does not wipe the other dots.
 *
 * `patchCell` applies an optimistic change (level, owner, descoped) to one cell
 * and recomputes the summary, so the drawer and counts move the instant an
 * action succeeds; the following `reload` replaces it with the server's truth.
 * `refreshing` is true while a reload runs behind data already on screen.
 */

export interface DiscoveryGridPayload extends DiscoveryGrid {
  companyId: string;
  engagementId: string | null;
  canWrite: boolean;
  viewerRole: 'consultant' | 'client';
  viewerId?: string;
  recommended: string[];
  lastSeenAt: string | null;
  changedCount: number;
}

export interface UseDiscoveryGridOptions {
  companyId: string;
  engagementId?: string | null;
  accessToken: string;
  spaceId?: string | null;
  spaceType?: 'global' | 'space' | null;
  enabled?: boolean;
}

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

export function useDiscoveryGrid({ companyId, engagementId, accessToken, spaceId, spaceType, enabled = true }: UseDiscoveryGridOptions) {
  const [data, setData] = useState<DiscoveryGridPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);
  // Cells flagged as changed on this session's first load, keyed by company+engagement.
  const sessionChanged = useRef<{ scope: string; ids: Set<string> } | null>(null);

  const reload = useCallback(async () => {
    if (!enabled || !companyId) {
      setLoading(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    try {
      const url = new URL(`${BASE}/discovery-grid/${companyId}`);
      if (engagementId) url.searchParams.set('engagementId', engagementId);
      if (spaceId) url.searchParams.set('spaceId', spaceId);
      if (spaceType) url.searchParams.set('spaceType', spaceType);
      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load the discovery grid (${res.status})`);
      }
      const json = await res.json();
      if (id !== requestId.current) return; // a newer request superseded this one

      const scope = `${companyId}:${json.engagementId ?? 'none'}`;
      const regions = json.regions ?? [];
      if (!sessionChanged.current || sessionChanged.current.scope !== scope) {
        // First load for this scope: remember the server's changed set, then record the visit.
        const ids = new Set<string>();
        for (const r of regions) for (const b of r.blocks ?? []) for (const c of b.cells ?? []) if (c.changed) ids.add(c.id);
        sessionChanged.current = { scope, ids };
        fetch(`${BASE}/discovery-grid/${companyId}/seen`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ engagementId: json.engagementId ?? null }),
        }).catch(() => { /* non-fatal */ });
      } else {
        // Later loads: keep the session's dots.
        const ids = sessionChanged.current.ids;
        for (const r of regions) for (const b of r.blocks ?? []) for (const c of b.cells ?? []) c.changed = c.changed || ids.has(c.id);
      }

      setData({
        companyId: json.companyId,
        engagementId: json.engagementId ?? null,
        summary: json.summary,
        regions,
        notActionable: json.notActionable ?? [],
        canWrite: !!json.canWrite,
        viewerRole: json.viewerRole === 'client' ? 'client' : 'consultant',
        viewerId: json.viewerId,
        recommended: Array.isArray(json.recommended) ? json.recommended : [],
        lastSeenAt: json.lastSeenAt ?? null,
        changedCount: sessionChanged.current?.ids.size ?? 0,
      });
      setError(null);
    } catch (e: any) {
      if (id !== requestId.current) return;
      console.error('[useDiscoveryGrid] load failed:', e);
      setError(e?.message || 'Failed to load the discovery grid');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [companyId, engagementId, accessToken, spaceId, spaceType, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const patchCell = useCallback((cellId: string, patch: Partial<GridCell>) => {
    setData((prev) => (prev ? patchGridCell(prev, cellId, patch) : prev));
  }, []);

  return { data, loading, error, reload, patchCell, refreshing: loading && data !== null };
}
